import { FC, useEffect, useState } from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';
import { Header } from '@/components/shared/Header/Header.tsx';
import DownloadIcon from '@/components/icons/DownloadIcon';
import PrintIcon from '@/components/icons/PrintIcon';

interface RecommendationData {
  id: number;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  domain: string;
}

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    paddingTop: 60,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 15,
    left: 20,
    right: 20,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 90,
    height: 22,
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    left: 20,
    right: 20,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
  },
  header: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
    color: '#1f2937',
  },
  introduction: {
    backgroundColor: '#dbeafe',
    borderLeftWidth: 4,
    borderLeftColor: '#3b82f6',
    padding: 15,
    marginBottom: 20,
  },
  introText: {
    fontSize: 12,
    color: '#1e40af',
    lineHeight: 1.4,
  },
  recommendationCard: {
    backgroundColor: '#f9fafb',
    padding: 15,
    marginBottom: 15,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1f2937',
  },
  recommendationDescription: {
    fontSize: 11,
    lineHeight: 1.4,
    marginBottom: 8,
    color: '#374151',
    textAlign: 'justify',
  },
  priorityBadge: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  domainBadge: {
    fontSize: 10,
    color: '#6b7280',
  },
});

// PDF Document Component
const RecommendationsPDFDocument: FC<{ recommendations: RecommendationData[] }> = ({ recommendations }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.headerBar}>
        <PDFLogo width={90} height={22} />
      </View>
      <Text style={pdfStyles.header}>Recommendations & Action Steps</Text>
      
      <View style={pdfStyles.introduction}>
        <Text style={pdfStyles.introText}>
          Your Personalized Action Plan{'\n\n'}
          Based on your assessment results, we've identified key areas for organizational development. 
          These recommendations are prioritized to help you build capacity systematically and effectively.
        </Text>
      </View>

      {recommendations.map((recommendation) => (
        <View key={recommendation.id} style={pdfStyles.recommendationCard}>
          <Text style={pdfStyles.recommendationTitle}>{recommendation.title}</Text>
          <Text style={[pdfStyles.priorityBadge, { 
            color: recommendation.priority === 'high' ? '#dc2626' : 
                   recommendation.priority === 'medium' ? '#d97706' : '#16a34a' 
          }]}>
            {recommendation.priority.charAt(0).toUpperCase() + recommendation.priority.slice(1)} Priority
          </Text>
          <Text style={pdfStyles.recommendationDescription}>{recommendation.description}</Text>
          <Text style={pdfStyles.domainBadge}>Domain: {recommendation.domain}</Text>
        </View>
      ))}
      <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
    </Page>
  </Document>
);

const RecommendationsPage: FC = () => {
  const [recommendations, setRecommendations] = useState<RecommendationData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      
      // Mock data for general recommendations
      const mockRecommendations: RecommendationData[] = [
        {
          id: 1,
          title: 'Strengthen Organizational Infrastructure',
          description: 'Focus on foundational elements like bylaws, incorporation, and business regulations to create a solid organizational foundation.',
          priority: 'high',
          domain: 'Organizational Infrastructure'
        },
        {
          id: 2,
          title: 'Develop Strategic Planning Framework',
          description: 'Implement a comprehensive strategic planning process to guide organizational direction and decision-making.',
          priority: 'high',
          domain: 'Strategic Planning'
        },
        {
          id: 3,
          title: 'Enhance Financial Management Systems',
          description: 'Establish robust financial tracking, budgeting, and reporting systems to ensure fiscal responsibility.',
          priority: 'medium',
          domain: 'Financial Management'
        },
        {
          id: 4,
          title: 'Build Leadership Capacity',
          description: 'Invest in leadership development programs to strengthen organizational management and governance.',
          priority: 'medium',
          domain: 'Leadership Development'
        },
        {
          id: 5,
          title: 'Improve Community Engagement',
          description: 'Develop strategies to better connect with and serve your target community.',
          priority: 'low',
          domain: 'Community Relations'
        }
      ];

      setRecommendations(mockRecommendations);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handlePrint = async (): Promise<void> => {
    try {
      const pdfDoc = <RecommendationsPDFDocument recommendations={recommendations} />;
      const asPdf = pdf(pdfDoc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (error) {
      console.error('Error generating PDF for print:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-semibold">Loading recommendations...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        backUrl="/reports"
        title="Recommendations & Action Steps"
        rightContent={
          <div className="flex flex-row gap-2">
            <PDFDownloadLink
              document={<RecommendationsPDFDocument recommendations={recommendations} />}
              fileName="recommendations-action-steps.pdf"
              className="bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              {({ loading: pdfLoading }: { loading: boolean }) => (
                <>
                  <DownloadIcon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm sm:text-base">
                    {pdfLoading ? 'Generating...' : 'Download PDF'}
                  </span>
                </>
              )}
            </PDFDownloadLink>
            
            <button
              onClick={handlePrint}
              className="bg-[#5E9E2E] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors flex items-center justify-center gap-2"
              type="button"
            >
              <PrintIcon className="w-5 h-5" />
              <span className="hidden sm:inline text-sm sm:text-base">Print PDF</span>
            </button>
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Introduction */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Your Personalized Action Plan</strong><br />
                Based on your assessment results, we've identified key areas for organizational development. 
                These recommendations are prioritized to help you build capacity systematically and effectively.
              </p>
            </div>
          </div>
        </div>

        {/* Recommendations List */}
        <div className="space-y-6">
          {recommendations.map((recommendation) => (
            <div key={recommendation.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {recommendation.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(recommendation.priority)}`}>
                      {recommendation.priority.charAt(0).toUpperCase() + recommendation.priority.slice(1)} Priority
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {recommendation.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500">Domain:</span>
                    <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {recommendation.domain}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                  View Detailed Action Steps
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-green-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Take Action?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            These recommendations are designed to help you build organizational capacity systematically. 
            Start with high-priority items and work your way through each domain at your own pace.
          </p>
          <div className="flex gap-4 justify-center">
            <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-md font-medium transition-colors">
              Download Full Report
            </button>
            <button className="bg-white hover:bg-gray-50 text-green-600 px-6 py-3 rounded-md font-medium border border-green-600 transition-colors">
              Schedule Consultation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationsPage; 